import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminSingleAssetHL.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const API = `${API_BASE}/disc-scalping`;

export default function AdminDiscScalpingSettings() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [form, setForm] = useState(null);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [feed, setFeed] = useState(null);
  const [effective, setEffective] = useState({ enabled: false, wallet: '' });
  const [userQuery, setUserQuery] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [accessBusy, setAccessBusy] = useState(null);
  const [accessMsg, setAccessMsg] = useState(null);
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { headers: authHeaders() });
      const { allowedUsers: au, feed: f, allowedUserIds, commissionEffective, commissionEffectiveWallet, ...rest } = data;
      setForm({ ...rest, allowedUserIds: allowedUserIds || [] });
      setEffective({ enabled: !!commissionEffective, wallet: commissionEffectiveWallet || '' });
      setAllowedUsers(au || []);
      setFeed(f || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeaders() });
      setOrders(data.orders || []);
    } catch {
      /* non-fatal */
    }
  }, []);

  // Every registered Omni account; desk access can only be granted to accounts that exist here.
  const loadUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
      setAllUsers(data.users || []);
      setUsersTotal(data.total ?? (data.users || []).length);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => {
    load();
    loadOrders();
    loadUsers();
    const t = setInterval(() => {
      axios.get(`${API}/feed-stats`, { headers: authHeaders() }).then(({ data }) => setFeed(data)).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [load, loadOrders, loadUsers]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setError(null);
    setSaveMsg(null);
    try {
      // Access is applied instantly from the Access card, so don't resend it here.
      const { allowedUserIds, ...payload } = form;
      const { data } = await axios.patch(`${API}/admin/settings`, payload, { headers: authHeaders() });
      setSaveMsg(data.message || 'Saved.');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  /** Grant/revoke desk access immediately (no Save needed). */
  const setAccess = async (u, allow) => {
    setAccessBusy(u.id);
    setAccessMsg(null);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/admin/access`, allow ? { add: [u.id] } : { remove: [u.id] }, { headers: authHeaders() });
      set('allowedUserIds', data.allowedUserIds || []);
      setAllowedUsers(data.allowedUsers || []);
      setAllUsers((list) => list.map((x) => (x.id === u.id ? { ...x, allowed: allow } : x)));
      setAccessMsg(`${u.email || `#${u.id}`} ${allow ? 'can now open the desk and chat.' : 'no longer has desk access.'}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setAccessBusy(null);
    }
  };

  const fmtLogin = (ts) => {
    if (!ts) return 'no login yet';
    const d = new Date(String(ts).includes('T') ? ts : `${String(ts).replace(' ', 'T')}Z`);
    if (Number.isNaN(d.getTime())) return String(ts);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    return days === 0 ? 'active today' : days === 1 ? 'active yesterday' : `last login ${days}d ago`;
  };

  const q = userQuery.trim().toLowerCase();
  const visibleUsers = allUsers.filter(
    (u) => !q || String(u.email || '').toLowerCase().includes(q) || String(u.discord_id || '').toLowerCase().includes(q)
  );

  if (loading || !form) return <div className="loading">Loading Discretionary Scalping settings…</div>;

  const num = (k, props = {}) => (
    <input type="number" step="any" value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} {...props} />
  );

  return (
    <div className="admin-single-asset-hl">
      <h2>🎯 Discretionary Scalping</h2>
      <p className="sahl-admin-note">
        Manual scalping desk on Hyperliquid. Shares the <strong>Single Asset HL wallet + agent</strong> per user, but has its
        own commission schedule and its own paper/live switch. Commission = <strong>notional × bps ÷ 10,000</strong>, charged
        per fill and sent from the trader&apos;s HL account (or accrued as owed).
      </p>

      {error && <div className="error-message">{error}</div>}
      {saveMsg && <div className="sahl-admin-success">{saveMsg}</div>}

      <form onSubmit={save} className="sahl-admin-form">
        <section className="sahl-admin-card">
          <h3>Desk status</h3>
          <label className="sahl-checkbox">
            <input type="checkbox" checked={!!form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Desk enabled (kill switch — unchecked blocks all new orders)
          </label>
          <label className="sahl-checkbox">
            <input type="checkbox" checked={!!form.paperMode} onChange={(e) => set('paperMode', e.target.checked)} />
            Default to paper mode for new traders (each trader can toggle paper/live themselves)
          </label>
          <div className="form-group">
            <label>Default order size (USD notional)</label>
            {num('defaultSizeUsd', { min: 10, step: 10 })}
          </div>
          <div className="form-group">
            <label>Default leverage</label>
            {num('defaultLeverage', { min: 1, max: 100, step: 1 })}
          </div>
          <div className="form-group">
            <label>Max leverage cap (desk-wide)</label>
            {num('maxLeverageCap', { min: 1, max: 100, step: 1 })}
          </div>
          <div className="form-group">
            <label>Default market-order slippage (bps)</label>
            {num('defaultSlippageBps', { min: 0, max: 500, step: 1 })}
          </div>
          <div className="form-group">
            <label>Max single order (USD notional)</label>
            {num('maxOrderUsd', { min: 100, step: 1000 })}
          </div>
        </section>

        <section className="sahl-admin-card sahl-commission-card">
          <h3>Scalping commission</h3>
          <label className="sahl-checkbox">
            <input type="checkbox" checked={!!form.commissionEnabled} onChange={(e) => set('commissionEnabled', e.target.checked)} />
            Commission enabled
          </label>
          <span className="field-hint">
            Effective: <strong>{effective.enabled ? 'charging' : 'not charging'}</strong>
            {effective.wallet ? ` → ${effective.wallet.slice(0, 6)}…${effective.wallet.slice(-4)}` : ' — no commission wallet configured here or in Single Asset'}
          </span>
          <div className="form-group">
            <label>Commission wallet (0x…) — blank = use Single Asset commission wallet</label>
            <input
              type="text"
              value={form.commissionWalletAddress || ''}
              onChange={(e) => set('commissionWalletAddress', e.target.value)}
              placeholder="0x…"
            />
          </div>
          <div className="form-group">
            <label>Buy side (bps)</label>
            {num('commissionBpsBuy', { min: 0, step: 0.5 })}
            <span className="field-hint">2 bps = $0.02 per $100 notional. Single Asset uses 10/15; scalping should stay low.</span>
          </div>
          <div className="form-group">
            <label>Sell side (bps)</label>
            {num('commissionBpsSell', { min: 0, step: 0.5 })}
          </div>
          <div className="form-group">
            <label>Minimum commission (USD)</label>
            {num('commissionMinUsd', { min: 0, step: 0.01 })}
          </div>
          <label className="sahl-checkbox">
            <input type="checkbox" checked={!!form.commissionBlockWhenOwed} onChange={(e) => set('commissionBlockWhenOwed', e.target.checked)} />
            Block live trading when owed commission exceeds
          </label>
          <div className="form-group">
            <label>Owed threshold (USD)</label>
            {num('commissionOwedBlockUsd', { min: 0, step: 0.5 })}
          </div>
        </section>

        <div className="sahl-admin-actions">
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </form>

      <section className="sahl-admin-card">
        <h3>Desk access — who can open the desk &amp; chat</h3>
        <p className="sahl-admin-note">
          Access is tied to an <strong>Omni account</strong>: a trader must have registered and be able to log in before
          they can be added (the list below is every account in the system). Admins always have access. Changes here
          apply immediately — no Save needed.
        </p>
        {accessMsg && <div className="sahl-admin-success">{accessMsg}</div>}

        <div className="dsc-access-grid">
          <div>
            <div className="dsc-access-title">
              Has desk access <span className="dsc-access-count">{allowedUsers.length}</span>
            </div>
            <ul className="dsc-admin-list">
              {allowedUsers.map((u) => (
                <li key={u.id}>
                  <span>
                    <strong>{u.email || `#${u.id}`}</strong>
                    {u.discord_id ? <small> · {u.discord_id}</small> : null}
                    <small> · {fmtLogin(u.last_login)}</small>
                  </span>
                  <button type="button" disabled={accessBusy === u.id} onClick={() => setAccess(u, false)}>
                    {accessBusy === u.id ? '…' : 'remove'}
                  </button>
                </li>
              ))}
              {!allowedUsers.length && <li className="dsc-admin-empty">No invited traders yet — only admins can open the desk.</li>}
            </ul>
          </div>

          <div>
            <div className="dsc-access-title">
              All Omni accounts <span className="dsc-access-count">{visibleUsers.length}{q ? ` / ${usersTotal}` : ''}</span>
            </div>
            <input
              type="text"
              className="dsc-access-filter"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              placeholder="Filter by email or Discord…"
            />
            <ul className="dsc-admin-list dsc-access-all">
              {visibleUsers.map((u) => {
                const allowed = u.allowed || form.allowedUserIds.includes(u.id);
                return (
                  <li key={u.id} className={u.is_admin ? 'is-admin' : allowed ? 'is-allowed' : ''}>
                    <span>
                      <strong>{u.email || `#${u.id}`}</strong>
                      {u.discord_id ? <small> · {u.discord_id}</small> : null}
                      <small> · {fmtLogin(u.last_login)}</small>
                      {u.status && u.status !== 'active' ? <small className="warn"> · {u.status}</small> : null}
                    </span>
                    {u.is_admin ? (
                      <em>admin · always allowed</em>
                    ) : allowed ? (
                      <button type="button" disabled={accessBusy === u.id} onClick={() => setAccess(u, false)}>
                        {accessBusy === u.id ? '…' : 'remove'}
                      </button>
                    ) : (
                      <button type="button" className="add" disabled={accessBusy === u.id} onClick={() => setAccess(u, true)}>
                        {accessBusy === u.id ? '…' : '+ add'}
                      </button>
                    )}
                  </li>
                );
              })}
              {!visibleUsers.length && <li className="dsc-admin-empty">{allUsers.length ? 'No accounts match that filter.' : 'Loading accounts…'}</li>}
            </ul>
          </div>
        </div>
      </section>

      <section className="sahl-admin-card">
        <h3>Market feed (shared Hyperliquid WebSocket)</h3>
        {feed ? (
          <div className="dsc-admin-feed">
            <div>WebSocket: <strong>{feed.status || '—'}</strong></div>
            <div>Coins subscribed: <strong>{feed.coins?.length ?? 0}</strong></div>
            <div>Browser streams: <strong>{(feed.coins || []).reduce((a, c) => a + (c.refs || 0), 0)}</strong></div>
            {Array.isArray(feed.coins) && feed.coins.length > 0 && (
              <ul>
                {feed.coins.map((c) => (
                  <li key={c.coin}>
                    {c.coin} · refs {c.refs} · subs {c.subs} · book {c.lastBookAt ? `${Math.round((Date.now() - c.lastBookAt) / 1000)}s ago` : '—'}
                    {c.error ? ` · error: ${c.error}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="dsc-admin-empty">No feed stats yet.</div>
        )}
      </section>

      <section className="sahl-admin-card">
        <h3>Recent desk orders (all traders)</h3>
        <div className="dsc-admin-table-wrap">
          <table className="dsc-admin-table">
            <thead>
              <tr><th>ID</th><th>User</th><th>Market</th><th>Side</th><th>Type</th><th>Size</th><th>Fill</th><th>Status</th><th>Mode</th><th>Time</th></tr>
            </thead>
            <tbody>
              {orders.slice(0, 60).map((o) => (
                <tr key={o.id}>
                  <td>{o.id}</td>
                  <td>{o.email || o.user_id}</td>
                  <td>{o.symbol}{o.dex && o.dex !== 'main' ? ` (${o.dex})` : ''}</td>
                  <td>{o.side}{o.reduce_only ? ' ro' : ''}</td>
                  <td>{o.order_type}</td>
                  <td>{o.filled_contracts || o.size_contracts}</td>
                  <td>{o.fill_price ?? '—'}</td>
                  <td title={o.error_message || ''}>{o.status}</td>
                  <td>{o.is_paper ? 'paper' : 'live'}</td>
                  <td>{o.executed_at || o.created_at}</td>
                </tr>
              ))}
              {!orders.length && <tr><td colSpan={10} className="dsc-admin-empty">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <style>{`
        .dsc-admin-list { list-style:none; padding:0; margin:8px 0 0; display:flex; flex-direction:column; gap:4px; }
        .dsc-admin-list li { display:flex; justify-content:space-between; align-items:center; padding:6px 10px; border-radius:6px; background:rgba(15,23,42,.6); font-size:.82rem; }
        .dsc-admin-list li button { background:none; border:0; color:#fca5a5; cursor:pointer; font:inherit; font-size:.75rem; white-space:nowrap; }
        .dsc-admin-list li button.add { color:#86efac; border:1px solid rgba(34,197,94,.4); border-radius:5px; padding:2px 8px; }
        .dsc-admin-list li button.add:hover:not(:disabled) { background:rgba(34,197,94,.15); }
        .dsc-admin-list li button:disabled { opacity:.5; cursor:default; }
        .dsc-admin-list li small { color:#94a3b8; font-size:.72rem; }
        .dsc-admin-list li small.warn { color:#fbbf24; }
        .dsc-admin-list li em { color:#94a3b8; font-size:.72rem; font-style:normal; white-space:nowrap; }
        .dsc-admin-list li.is-admin { opacity:.75; }
        .dsc-admin-list li.is-allowed { box-shadow: inset 2px 0 0 #22c55e; }
        .dsc-access-grid { display:grid; grid-template-columns: minmax(0,1fr) minmax(0,1fr); gap:16px; }
        @media (max-width: 900px) { .dsc-access-grid { grid-template-columns: 1fr; } }
        .dsc-access-title { font-size:.72rem; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; font-weight:700; margin-bottom:6px; display:flex; align-items:center; gap:6px; }
        .dsc-access-count { padding:0 6px; border-radius:999px; background:rgba(148,163,184,.15); font-size:.66rem; }
        .dsc-access-filter { width:100%; box-sizing:border-box; padding:7px 10px; border-radius:6px; border:1px solid rgba(148,163,184,.3); background:rgba(15,23,42,.6); color:#e2e8f0; font:inherit; font-size:.8rem; }
        .dsc-access-all { max-height: 360px; overflow-y:auto; }
        .dsc-admin-empty { color:#94a3b8; font-size:.8rem; }
        .dsc-admin-feed { font-size:.82rem; display:flex; flex-direction:column; gap:4px; }
        .dsc-admin-feed ul { margin:4px 0 0; padding-left:18px; color:#94a3b8; }
        .dsc-admin-table-wrap { overflow-x:auto; }
        .dsc-admin-table { width:100%; border-collapse:collapse; font-size:.76rem; }
        .dsc-admin-table th, .dsc-admin-table td { padding:5px 8px; border-bottom:1px solid rgba(148,163,184,.12); text-align:left; white-space:nowrap; }
        .dsc-admin-table th { color:#94a3b8; font-weight:600; text-transform:uppercase; font-size:.64rem; }
      `}</style>
    </div>
  );
}
